import { QuoteRatingSearchResponse } from '@lio/api-types';
import { Grid, Typography } from '@mui/material';
import { useGetPolicy } from '@services/policy';
import { fakeLocalMidnightFromTZMidnight, getDateFormatter, ShortDateFormat } from '@utils/date';
import { USDollar } from '@utils/number';
import { getFormattedPolicyNumber } from '@utils/policy/getFormattedPolicyNumber';
import { useGetScreenSize } from '@utils/useGetScreenSize';
import { format } from 'date-fns';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';

interface Props {
  policyCoverage: string;
  policyId?: string;
  estimatedPremium: string | number;
  quoteNumber: string;
  effectiveDate: Date | string;
  policyFee?: string | number | null;
  taxFeeSurcharge?: string | number | null;
  latestRatingResults?: QuoteRatingSearchResponse['items'][0];
  isEndorsement: boolean;
  agentCommission?: string | number | null;
}

export const QuoteProposalCardRow = ({
  policyCoverage,
  policyId,
  estimatedPremium,
  quoteNumber,
  effectiveDate,
  policyFee,
  taxFeeSurcharge,
  latestRatingResults,
  isEndorsement,
  agentCommission,
}: Props) => {
  const [layoutPreferences] = useStore((state) => [state.layoutPreferences], shallow);
  const clockFormat = layoutPreferences.clockFormat;
  const { data: policy } = useGetPolicy({ policyId: isEndorsement ? policyId : undefined });
  const policyNumber = policy
    ? getFormattedPolicyNumber(policy.policyNumberPrefix, policy.policyNumber.policyNumber)
    : '...';
  const { isMobileOrSmallScreen } = useGetScreenSize();
  const mobileRows = isMobileOrSmallScreen ? { display: 'flex', justifyContent: 'space-between', mb: 0.5 } : {};
  const hasPolicyFee = policyFee !== undefined && policyFee !== null && !isEndorsement;
  const hasTaxFeeSurcharge = taxFeeSurcharge !== undefined && taxFeeSurcharge !== null && !isEndorsement;
  const hasAgentCommission = agentCommission !== undefined && agentCommission !== null;

  // Calculate number of optional columns
  const optionalColumnCount = [hasPolicyFee, hasTaxFeeSurcharge, hasAgentCommission].filter(Boolean).length;
  // Base items = 4 (Policy, Premium, Quote#, Effective Date), plus optional columns
  const totalItems = 4 + optionalColumnCount;
  // Calculate size per item (12 / totalItems), rounded to one decimal place
  const sizePerItem = Math.floor((12 / totalItems) * 10) / 10;
  const sizing = { xs: 12, sm: 12, md: sizePerItem, lg: sizePerItem, xl: sizePerItem };

  // adjust to make labels fit on one line
  const labelFontSize = optionalColumnCount > 0 ? 12 : 14;

  return (
    <>
      <Grid
        container
        className='quote-proposal-card-item'
        sx={{ display: 'flex', width: '100%' }}
        size={{ xs: 12, sm: 12, md: 12 }}
      >
        <Grid size={sizing} sx={mobileRows}>
          <Typography variant='bodySmall' sx={{ fontSize: labelFontSize, display: { lg: 'block' } }}>
            Policy
          </Typography>
          <Typography variant='h6'>{policyCoverage}</Typography>
        </Grid>
        <Grid size={sizing} sx={mobileRows}>
          <Typography variant='bodySmall' sx={{ fontSize: labelFontSize, mr: '10px', display: { lg: 'block' } }}>
            {isEndorsement ? 'Endorsement Premium Change' : 'Estimated Premium'}
          </Typography>
          <Typography variant='h6' data-testid='Estimated Premium'>
            {isEndorsement
              ? latestRatingResults?.endorsementDetails?.endorsementPremiumChange
                ? USDollar.format(latestRatingResults?.endorsementDetails?.endorsementPremiumChange)
                : '-'
              : USDollar.format(Number(estimatedPremium))}
          </Typography>
        </Grid>
        {hasPolicyFee ? (
          <Grid size={sizing} sx={mobileRows}>
            <Typography variant='bodySmall' sx={{ fontSize: labelFontSize, display: { lg: 'block' } }}>
              Policy Fee
            </Typography>
            <Typography variant='h6' data-testid='Policy Fee'>
              {policyFee}
            </Typography>
          </Grid>
        ) : null}
        {hasTaxFeeSurcharge ? (
          <Grid size={sizing} sx={mobileRows}>
            <Typography variant='bodySmall' sx={{ fontSize: labelFontSize, display: { lg: 'block' } }}>
              Taxes, Fees, Surcharges
            </Typography>
            <Typography variant='h6' data-testid='Taxes Fees Surcharges'>
              {taxFeeSurcharge}
            </Typography>
          </Grid>
        ) : null}
        <Grid size={sizing} sx={mobileRows}>
          <Typography variant='bodySmall' sx={{ fontSize: labelFontSize, display: { lg: 'block' } }}>
            {isEndorsement ? 'Policy #' : 'Quote #'}
          </Typography>
          <Typography variant='h6' data-testid='Quote number'>
            {isEndorsement ? policyNumber : quoteNumber}
          </Typography>
        </Grid>
        <Grid size={sizing} sx={mobileRows}>
          <Typography variant='bodySmall' sx={{ fontSize: labelFontSize, display: { lg: 'block' } }}>
            {isEndorsement ? 'Transaction Effective Date' : 'Policy Effective Date'}
          </Typography>
          <Typography variant='h6' data-testid='Effective Date'>
            {format(
              fakeLocalMidnightFromTZMidnight(new Date(effectiveDate)),
              getDateFormatter(clockFormat, ShortDateFormat),
            )}
          </Typography>
        </Grid>
        {hasAgentCommission ? (
          <Grid size={sizing} sx={mobileRows}>
            <Typography variant='bodySmall' sx={{ fontSize: labelFontSize, display: { lg: 'block' } }}>
              Agent Commission
            </Typography>
            <Typography variant='h6' data-testid='Agent Commission'>
              {typeof agentCommission === 'number' ? `${(agentCommission * 100).toFixed(0)}%` : agentCommission}
            </Typography>
          </Grid>
        ) : null}
      </Grid>
      {isEndorsement ? (
        <Grid
          container
          sx={{
            marginTop: '24px',
            marginBottom: '16px',
          }}
        >
          <Grid size={{ xs: 12, sm: 12 }} sx={mobileRows}>
            <Typography variant='bodySmall' sx={{ fontSize: labelFontSize, display: { lg: 'block' } }}>
              New Total Policy Premium{' '}
            </Typography>
            <Typography
              variant='bodySmall'
              fontWeight='bold'
              data-testid='New Policy Premium'
              sx={{
                color: 'text.main',
              }}
            >
              {latestRatingResults?.endorsementDetails?.newPolicyCost
                ? USDollar.format(latestRatingResults?.endorsementDetails?.newPolicyCost)
                : '-'}
            </Typography>
          </Grid>
          {/* TEMPORARILY HIDDEN FOR NOW as we do not have this */}
          {/* <Grid size={{ xs: 12, sm: 3 }} sx={mobileRows}>
            <Typography variant='caption' sx={{ display: { lg: 'block' } }}>
              Annual Pro-Rata Factor:
            </Typography>
            <Typography
              variant='bodySmall'
              data-testid='Pro-Rata Factor'
              sx={{
                color: 'common.black',
              }}
            >
              -
            </Typography>
          </Grid> */}
        </Grid>
      ) : null}
    </>
  );
};
